import lobData from '../../assets/lob-cards.json'
import {
  ALBUM_PREFIXES,
  PACK_LOGIC,
  SHOP_PACK_ID,
  STARTER_PACK_IDS,
  albumNumericPrinting,
  albumPrefixOf,
  asCardFoil,
  cardsInPack,
  emptyPackCounts,
  packPrintedRarity,
  pickAlbumSet,
  printingsFromSets,
  type AlbumSetPrefix,
  type CardFoilId,
  type PackCardHint,
  type PackId,
  type PackPool,
  type PackPullWire,
  type PrintedRarity,
} from '@shared/index'

export { ALBUM_PREFIXES, type AlbumSetPrefix }

export type { PackId, PackPool, PackPullWire }

export interface CatalogCard {
  id: string
  name: string
  type: string
  frameType?: string
  typeline?: string[]
  atk?: number
  def?: number
  level?: number
  attribute?: string
  race?: string
  desc?: string
  [key: string]: unknown
}

export interface PackDef {
  id: PackId
  name: string
  tagline: string
  size: number
  kind: 'starter' | 'shop' | 'promo'
  pool: PackPool
  cover: string
}

export interface PackPull {
  cardId: string
  card: CatalogCard
  rarity: CardFoilId
  isNew: boolean
}

interface RawSet {
  set_name?: string
  set_code?: string
  set_rarity?: string
}

interface RawCard {
  id: string | number
  name: string
  type?: string
  frameType?: string
  typeline?: string[]
  card_sets?: RawSet[]
  [key: string]: unknown
}

export type LobRarity = 'common' | 'rare' | 'super' | 'ultra' | 'secret'

const RAW = (lobData as { data: RawCard[] }).data
const RAW_BY_ID = new Map(RAW.map((card) => [String(card.id), card]))

const CATALOG: CatalogCard[] = RAW.map((card) => ({
  ...card,
  id: String(card.id),
  name: card.name,
  type: card.type || '',
}))

const BY_ID = new Map(CATALOG.map((card) => [card.id, card]))

const PACK_HINTS: PackCardHint[] = RAW.map((card) => {
  const hint: PackCardHint = {
    id: String(card.id),
    type: card.type || '',
    printings: printingsFromSets(card.card_sets, ALBUM_PREFIXES),
  }
  if (card.frameType) hint.frameType = card.frameType
  if (card.typeline) hint.typeline = card.typeline
  const human = card.humanReadableCardType
  if (typeof human === 'string') hint.humanReadableCardType = human
  return hint
})

export type PackListEntry = {
  id: string
  rarity: PrintedRarity
}

/** Cards that can come out of this pack, with the rarity the pack uses. */
export function packListEntries(packId: PackId): PackListEntry[] {
  return cardsInPack(packId, PACK_HINTS).map((card) => ({
    id: String(card.id),
    rarity: packPrintedRarity(card),
  }))
}

function pickedSet(card: RawCard): RawSet | undefined {
  return pickAlbumSet(card.card_sets ?? [])
}

/**
 * Display set code: LOB-xxx when the card is in LOB, else SDY/SDK NA code.
 */
export function lobSetCode(cardId: string): string {
  const raw = RAW_BY_ID.get(cardId)
  return pickedSet(raw ?? { id: cardId, name: '' })?.set_code ?? ''
}

/**
 * Numeric collector number (LOB-027 → 27, SDY-001 → 1).
 * Cards without a parseable code sort last within their prefix.
 */
export function setCodeNumber(code: string): number {
  if (code.length < 3) return 9999
  const n = Number(code.slice(-3))
  return Number.isFinite(n) ? n : 9999
}

export function lobSetNumber(cardId: string): number {
  return setCodeNumber(lobSetCode(cardId))
}

/**
 * NA collector code of this card in one set (`MRD` → `MRD-001`).
 * Empty when that set did not print the card.
 */
export function setPrintingCode(cardId: string, prefix: string): string {
  const raw = RAW_BY_ID.get(cardId)
  return albumNumericPrinting(raw?.card_sets ?? [], prefix)?.set_code ?? ''
}

/**
 * Album sort rank: LOB (0), then SDY (1), then SDK (2), then MRD (3).
 */
export function albumSetRank(cardId: string): number {
  const prefix = albumSetPrefix(cardId)
  const i = (ALBUM_PREFIXES as readonly string[]).indexOf(prefix)
  return i >= 0 ? i : ALBUM_PREFIXES.length
}

/** Home-set prefix. The All chip sorts by this. A set chip uses `setPrintingCode`. */
export function albumSetPrefix(cardId: string): string {
  return albumPrefixOf(lobSetCode(cardId))
}

/**
 * Print rarity of the album set (LOB reprint preferred). Short Print = Common.
 */
export function lobRarity(cardId: string): LobRarity {
  const raw = RAW_BY_ID.get(cardId)
  const rarity = (pickedSet(raw ?? { id: cardId, name: '' })?.set_rarity || '')
    .toLowerCase()
  if (rarity.includes('secret')) return 'secret'
  if (rarity.includes('ultra')) return 'ultra'
  if (rarity.includes('super')) return 'super'
  if (rarity.includes('short')) return 'common'
  if (rarity.includes('rare')) return 'rare'
  return 'common'
}

export const RARITY_LABEL: Record<CardFoilId, string> = {
  common: 'Common',
  rare: 'Rare',
  super: 'Super Rare',
  ultra: 'Ultra Rare',
  ghost: 'Ghost Rare',
  helios: 'Helios Rare',
  nightglass: 'Nightglass Rare',
  stormwake: 'Stormwake Rare',
}

const LOB_NAME = 'Legend of Blue Eyes White Dragon'
const SWORDS_NAME = 'Swords of Light'
const GAIA_NAME = 'Gaia the Dragon Champion'
const MASTER_NAME = 'Master Pack'
const MASTER_COVER = '/assets/packs/pack-master.webp'
const PROMO_COVER = '/assets/packs/lob.jpg'
const PROMO_NAME = 'Promo Pack'
const SET_MRD_COVER = '/assets/home/metal-raiders.webp'
const SET_MRD_NAME = 'Metal Raiders Pack'
const PROMO_MRD_NAME = 'Metal Raiders Special Pack'

const COVERS: Record<PackId, string> = {
  'starter-beasts': '/assets/packs/pack-starter-monsters.webp?v=3',
  'starter-runes': '/assets/packs/pack-starter-spells.webp?v=3',
  'starter-crucible': '/assets/packs/pack-starter-extra.webp?v=3',
  'shop-onyx': MASTER_COVER,
  'set-lob': PROMO_COVER,
  'promo-lob': PROMO_COVER,
  'set-mrd': SET_MRD_COVER,
  'promo-mrd': SET_MRD_COVER,
}

const NAMES: Record<PackId, string> = {
  'starter-beasts': LOB_NAME,
  'starter-runes': SWORDS_NAME,
  'starter-crucible': GAIA_NAME,
  'shop-onyx': MASTER_NAME,
  'set-lob': LOB_NAME,
  'promo-lob': PROMO_NAME,
  'set-mrd': SET_MRD_NAME,
  'promo-mrd': PROMO_MRD_NAME,
}

const TAGLINES: Record<PackId, string> = {
  'starter-beasts': 'Starter · 20 monsters · Blue-Eyes',
  'starter-runes': 'Starter · 20 spells and traps · Swords',
  'starter-crucible': 'Starter · 5 Extra Deck · Gaia',
  'shop-onyx': 'Special · 5 cards from the whole set',
  'set-lob': '9 cards · Legend of Blue-Eyes and the starter decks',
  'promo-lob': 'Promo · 1 Blue-Eyes White Dragon',
  'set-mrd': '9 cards · Metal Raiders',
  'promo-mrd': 'Special · 5 Metal Raiders cards · Ultra Rare or higher',
}

export const PACKS: PackDef[] = PACK_LOGIC.map((pack) => ({
  ...pack,
  name: NAMES[pack.id],
  tagline: TAGLINES[pack.id],
  cover: COVERS[pack.id],
}))

export const PACK_BY_ID: Record<PackId, PackDef> = Object.fromEntries(
  PACKS.map((pack) => [pack.id, pack]),
) as Record<PackId, PackDef>

export { STARTER_PACK_IDS, SHOP_PACK_ID, emptyPackCounts }

export function getCatalogCard(cardId: string): CatalogCard | undefined {
  return BY_ID.get(cardId)
}

export function allCatalogCards(): CatalogCard[] {
  return CATALOG
}

/**
 * Overlay payload from a server pack roll.
 */
export function toPulls(wires: PackPullWire[]): PackPull[] {
  return wires.map((pull) => {
    const foil = asCardFoil(pull.foil)
    const card = BY_ID.get(pull.cardId)
    return {
      cardId: pull.cardId,
      card: {
        ...(card ?? { id: pull.cardId, name: pull.cardId, type: '' }),
        foil,
      },
      rarity: foil,
      isNew: pull.isNew,
    }
  })
}
